import React, { useMemo, useState } from "react";
import { Unavailable } from "./FootballPitch";
import ShotExplorer from "./ShotExplorer";
import HeatmapExplorer from "./HeatmapExplorer";
import NetworkExplorer from "./NetworkExplorer";
import CompareView from "./CompareView";
import {
  AttackDefenceTrend,
  FormStrip,
  MetricGroups,
  TacticalProfile,
  VenueSplit,
  opponentOf,
} from "./TeamPanels";
import { exposureSummary, formatMetric, formatNumber, metricLabel, resultOf } from "./fiData";
import "./football-intelligence.css";
import "./fi-extra.css";

const PLAYER_HEADLINE = ["goals", "assists", "npxg", "total_shots", "passes_attempted", "progressive_passes", "progressive_carries", "xag"];
const TEAM_HEADLINE = ["xgf", "xga", "shots_for", "corners_for", "corners_against", "territory.box_entries", "territory.field_tilt", "defending.ppda"];
const PLAYER_EXTRA = ["accurate_passes", "tackles", "interceptions", "recoveries", "fouls"];
const SAMPLE_SIZES = [5, 10, 20, 50, 1000];

/** Per-match bars for one metric, oldest to latest. */
function Trend({ rows, metric }) {
  const series = [...rows].reverse().map((row) => ({ row, value: metric === "minutes" ? row.minutes : row.values?.[metric] }));
  const max = Math.max(1, ...series.map((x) => x.value ?? 0));
  return (
    <div className="fi-trend" role="img" aria-label={`${metricLabel(metric)} by match`}>
      {series.map(({ row, value }) => (
        <div key={row.fixture_id} title={`${row.fixture?.name}: ${value ?? "unavailable"}`}>
          <span>{formatNumber(value)}</span>
          <i style={{ height: value == null ? 2 : Math.max(2, (value / max) * 110), opacity: value == null ? 0.25 : 1 }} />
          <small>{new Date(row.kickoff_utc).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</small>
        </div>
      ))}
    </div>
  );
}

export function FootballAnalyticsView({
  data,
  kind = "player",
  filters = {},
  onFilter = () => {},
  compare = null,
  initialTab = "Overview",
  embedded = false,
  compareSlot = null,
}) {
  const [tab, setTab] = useState(initialTab);
  const [per90, setPer90] = useState(false);
  const player = kind === "player";
  const [metric, setMetric] = useState(player ? (data?.metrics?.total_shots ? "total_shots" : "passes_attempted") : "xgf");
  const [imageFailed, setImageFailed] = useState(false);
  const [query, setQuery] = useState("");

  const rows = useMemo(() => data?.matches || [], [data]);
  const exposure = useMemo(() => exposureSummary(rows), [rows]);

  if (!data || data.status === "UNAVAILABLE") {
    const text = data?.reason?.replace(/_/g, " ") || "No verified analytics available.";
    if (embedded) {
      return (
        <section className="fi-shell fi-embedded">
          <div className="fx-collapsed"><span aria-hidden="true">◌</span><span>Advanced football analytics: {text.toLowerCase()}. <a href="/admin/football-research">Open research lab</a></span></div>
        </section>
      );
    }
    return <section className="fi-shell"><Unavailable text={text} /></section>;
  }

  const publicActuals = data.rights === 'COMMERCIAL_ANALYTICS_APPROVED';
  const preferred = player ? PLAYER_HEADLINE : TEAM_HEADLINE;
  const available = [...preferred, ...(player ? PLAYER_EXTRA : [])].filter((k, i, all) => all.indexOf(k) === i && data.metrics?.[k]);
  const headline = available.length ? available.slice(0, 8) : preferred;
  const tabs = ["Overview", "All statistics", ...(player ? [] : ["Tactics"]), "Shot map", "Action density", ...(player ? [] : ["Passing network"]), "Match log", ...(compare ? ["Compare"] : [])];
  const value = (stat, key) => (stat ? formatMetric(key, player && per90 ? stat.per90 : stat.value, !player) : "—");
  const matchName = (id) => rows.find((r) => String(r.fixture_id) === String(id))?.fixture?.name || "";
  const matchOptions = rows.map((r) => ({ fixture_id: r.fixture_id, name: r.fixture?.name || String(r.fixture_id) }));

  return (
    <section className={`fi-shell ${embedded ? "fi-embedded" : ""}`}>
      <header className="fi-hero">
        {!embedded ? (
          <div className="fi-profile">
            {data.profile?.image_url && !imageFailed
              ? <img src={data.profile.image_url} alt="" onError={() => setImageFailed(true)} />
              : <div className="fi-monogram">{data.profile?.name?.slice(0, 2)}</div>}
            <div>
              <div className="fi-eyebrow">CSB FOOTBALL INTELLIGENCE · {publicActuals ? 'RECORDED STATISTICS' : 'PRIVATE RESEARCH'}</div>
              <h2>{data.profile?.name || "Football intelligence"}</h2>
              <p>Historical actuals · {data.sample_size ?? 0} {data.sample_size === 1 ? "match" : "matches"}{player ? ` · ${exposure.minutes} recorded minutes` : ""}</p>
            </div>
          </div>
        ) : (
          <div>
            <div className="fi-eyebrow">{publicActuals ? 'RECORDED MATCH STATISTICS' : 'PRIVATE · VERIFIED SOURCE ACTUALS'}</div>
            <h2>Advanced football analytics</h2>
            <p>{data.sample_size ?? 0} recorded matches{player ? ` · ${exposure.minutes} minutes` : ""} · {data.profile?.name}</p>
          </div>
        )}
        <div className="fi-source">PITCHAPI<span>Source definitions retained</span></div>
      </header>

      <div className="fi-controls">
        <label>Season<select aria-label="Season" value={filters.season || ""} onChange={(e) => onFilter("season", e.target.value)}>
          <option value="">All cached seasons</option>{data.seasons?.map((s) => <option key={s}>{s}</option>)}</select></label>
        <label>Competition<select aria-label="Competition" value={filters.competition || ""} onChange={(e) => onFilter("competition", e.target.value)}>
          <option value="">All cached competitions</option>{data.competitions?.map((s) => <option key={s} value={s}>{data.competition_names?.[s] || s}</option>)}</select></label>
        <label>Venue<select aria-label="Venue" value={filters.venue || ""} onChange={(e) => onFilter("venue", e.target.value)}>
          <option value="">Home & away</option><option value="home">Home</option><option value="away">Away</option></select></label>
        <label>Match<select aria-label="Match" value={filters.match || ""} onChange={(e) => onFilter("match", e.target.value)}>
          <option value="">All sampled matches</option>{data.matches?.map((r) => <option key={r.fixture_id} value={r.fixture_id}>{r.fixture?.name || r.fixture_id}</option>)}</select></label>
        <label>Sample<select aria-label="Sample" value={filters.last_n || 10} onChange={(e) => onFilter("last_n", e.target.value)}>
          {SAMPLE_SIZES.map((n) => <option key={n} value={n}>{n === 1000 ? "Full cached season (≤1,000)" : `Last ${n} available`}</option>)}</select></label>
        {compareSlot}
        {player && <button className={per90 ? "fi-toggle active" : "fi-toggle"} onClick={() => setPer90(!per90)}>{per90 ? "Per 90" : "Totals"} ↔</button>}
      </div>

      <nav className="fi-tabs" aria-label="Analytics sections">
        {tabs.map((t) => <button aria-pressed={tab === t} key={t} onClick={() => setTab(t)}>{t}</button>)}
      </nav>

      {data.status === "NO_MATCHING_SAMPLE" ? (
        <Unavailable text="No matches satisfy these filters. Change the season or sample." />
      ) : (
        <>
          {tab === "Overview" && (
            <>
              <div className="fi-stats">
                {headline.map((k) => (
                  <article key={k}>
                    <span>{metricLabel(k)}</span>
                    <strong>{value(data.metrics?.[k], k)}</strong>
                    <small>{data.metrics?.[k] ? `${data.metrics[k].sample} sampled matches · ${per90 && player ? "per 90" : data.metrics[k].unit}` : "Unavailable for this sample"}</small>
                  </article>
                ))}
              </div>

              {player && (
                <div className={`fx-callout ${exposure.smallSample ? "warn" : ""}`} style={{ margin: "0 28px 18px" }}>
                  <strong>Reading per-90 values.</strong> Built from {exposure.withMinutes} of {exposure.appearances} sampled appearances that recorded minutes ({exposure.minutes} minutes).
                  {exposure.smallSample && " This is under 450 minutes, so per-90 rates are volatile."}
                  {exposure.advancedIncompatible > 0 && ` The provider flags advanced metrics as not minutes-compatible for ${exposure.advancedIncompatible} of ${exposure.appearances} appearances.`}
                </div>
              )}

              {!player && (
                <div className="fx-two">
                  <article className="fi-card"><h3>Attack vs defence</h3><p className="fx-sub">Expected goals for and against in each sampled match.</p><AttackDefenceTrend matches={rows} /></article>
                  <article className="fi-card"><h3>Recent results</h3><p className="fx-sub">From the recorded scoreline of each sampled match.</p><FormStrip matches={rows} detail={5} /></article>
                </div>
              )}

              <div className="fi-columns">
                <article className="fi-card">
                  <h3>Match-by-match performance</h3>
                  <select aria-label="Trend metric" value={metric} onChange={(e) => setMetric(e.target.value)}>
                    {(player ? ["total_shots", "passes_attempted", "goals", "assists", "npxg", "tackles", "minutes"] : ["xgf", "xga", "corners_for", "corners_against"]).map((k) => <option key={k} value={k}>{metricLabel(k)}</option>)}
                  </select>
                  <Trend rows={rows} metric={metric} />
                </article>
                <article className="fi-card">
                  <h3>The evidence behind the numbers</h3>
                  <p>{data.date_range?.map((d) => new Date(d).toLocaleDateString("en-GB")).join(" — ") || "Date range unavailable"}</p>
                  <p>These are the last available cached matches, not guaranteed complete fixtures. Match-level samples accompany each metric.</p>
                  <p>Progressive carries may be synthesized. Possession measures may represent pass share. Shots on target remain unavailable pending definition reconciliation.</p>
                  <span className="fi-badge">Historical actuals · not predictions</span>
                </article>
              </div>
            </>
          )}

          {tab === "All statistics" && (
            <>
              <div className="fx-pad">
                <div className="fx-toolbar">
                  <label className="fx-field">Find a metric
                    <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="e.g. xG, passes, PPDA" aria-label="Find a metric" />
                  </label>
                  <span className="fx-coverage"><b>{Object.keys(data.metrics || {}).length}</b> source metrics · {player && per90 ? "per 90" : player ? "totals" : "match means"} · trend = each sampled match</span>
                </div>
              </div>
              <MetricGroups kind={kind} metrics={data.metrics || {}} matches={rows} per90={per90} query={query} />
            </>
          )}

          {tab === "Tactics" && !player && (
            <>
              <div className="fx-two">
                <article className="fi-card"><h3>Where the team operates</h3><p className="fx-sub">Provider average heights and attack style, match means.</p><TacticalProfile metrics={data.metrics} /></article>
                <article className="fi-card"><h3>Home vs away</h3><p className="fx-sub">Means over the sampled matches at each venue.</p><VenueSplit matches={rows} /></article>
              </div>
              <div className="fx-pad">
                <p className="fi-note">
                  Spatial detail: action-density grids for {data.heatmaps?.length ?? 0} and passing networks for {data.networks?.length ?? 0} of {data.sample_size ?? 0} sampled matches. Open the Action density and Passing network tabs to explore them.
                </p>
              </div>
            </>
          )}

          {tab === "Shot map" && (
            <article className="fi-card"><h3>Chance quality & shot locations</h3><ShotExplorer shots={data.shots} matchOptions={matchOptions} /></article>
          )}
          {tab === "Action density" && (
            <article className="fi-card"><h3>Observed action density</h3><HeatmapExplorer heatmaps={data.heatmaps || []} matchName={matchName} sampleSize={data.sample_size} /></article>
          )}
          {tab === "Passing network" && (
            <article className="fi-card"><h3>Passing connections</h3><NetworkExplorer networks={data.networks || []} matchName={matchName} sampleSize={data.sample_size} /></article>
          )}

          {tab === "Match log" && (
            <div className="fx-pad">
              <div className="fx-scroll" style={{ maxHeight: rows.length > 20 ? 560 : "none" }}>
                <table>
                  <thead>
                    <tr>
                      <th>Date</th><th>Fixture</th><th>Venue</th>
                      <th>{player ? "Minutes" : "Result"}</th><th className="fx-num">{player ? "Shots" : "xG for"}</th><th className="fx-num">{player ? "Passes" : "xG against"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const result = resultOf(r);
                      return (
                        <tr key={r.fixture_id}>
                          <td>{new Date(r.kickoff_utc).toLocaleDateString("en-GB")}</td>
                          <td>{r.fixture?.name || r.fixture_id}{opponentOf(r) && <small>vs {opponentOf(r)}</small>}</td>
                          <td>{r.home ? "Home" : "Away"}</td>
                          <td>{player ? formatNumber(r.minutes) : <><span className={`fx-wdl ${result || "none"}`} style={{ width: 22, height: 22, marginRight: 8 }}>{result || "–"}</span>{result ? `${r.goals}–${r.conceded}` : "—"}</>}</td>
                          <td className="fx-num">{formatNumber(r.values?.[player ? "total_shots" : "xgf"])}</td>
                          <td className="fx-num">{formatNumber(r.values?.[player ? "passes_attempted" : "xga"])}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === "Compare" && compare && (
            <article className="fi-card">
              <h3>{data.profile?.name} / {compare.profile?.name}</h3>
              <CompareView a={data} b={compare} kind={kind} per90={per90} />
            </article>
          )}
        </>
      )}

      <footer className="fi-provenance">
        Source: {data.source} · retrieved {data.latest_observed_at ? new Date(data.latest_observed_at).toLocaleString("en-GB") : "unknown"} · {publicActuals ? 'Recorded actuals, not live tracking' : 'Retrospective, provider-revision-unsafe · Advanced data not approved for public use'}
      </footer>
    </section>
  );
}

export default FootballAnalyticsView;
